import { COMMANDS, LOGGER } from "$lib/commands";
import { Interaction } from "$lib/wasm/interaction";
import { Canvas } from "svelte-pixel-editor-wasm";
import { availableTools, symmetryGuideEditing } from "$lib/state";
import type { ToolId } from "$lib/panels/tools";

export function createInputHandler(
    node: HTMLElement,
    { int, canvas }: { int: Interaction; canvas: Canvas }
) {
    let originalTool: number | null = null;
    let switchKey: string | null = null;

    function getPos(e: { clientX: number; clientY: number }): Vec2 {
        //get position in percentage
        const rect = node.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        return canvas.canvas_pos_to_file_pixel(x * devicePixelRatio, y * devicePixelRatio);
    }

    function getGuidePos(e: { clientX: number; clientY: number }): Vec2 {
        const rect = node.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        return (canvas as any).canvas_pos_to_file_center2(
            x * devicePixelRatio,
            y * devicePixelRatio
        );
    }

    function onPointerDown(e: PointerEvent) {
        if (e.buttons & 2) {
            COMMANDS.invoke("editor.tool.cancel");
            return;
        }

        if (e.buttons !== 1) return;

        if (symmetryGuideEditing.value) {
            int.setSymmetryCenter2(getGuidePos(e));
            window.addEventListener("pointermove", onGuideMove);
            window.addEventListener("pointerup", onGuideUp);
            window.addEventListener("pointercancel", onGuideCancel);
            window.addEventListener("keydown", onGuideKeyDown);
            e.preventDefault();
            return;
        }

        if (e.ctrlKey || (e.altKey && !isMoveToolActive())) {
            const toolId = (e.ctrlKey ? "move" : "picker") satisfies ToolId;
            const targetTool = availableTools.value.find((t) => t.id === toolId);
            if (targetTool && int.tool !== targetTool.index && !int.getIsCurrentToolActive()) {
                originalTool = int.tool;
                switchKey = e.ctrlKey ? "Control" : "Alt";
                COMMANDS.invoke(`editor.tool.${targetTool.id}` as any);
            }
        }

        const pos = getPos(e);
        const info = {
            shift: e.shiftKey,
            ctrl: e.ctrlKey,
            alt: e.altKey,
        };

        int.pointerDown(pos, info);

        window.addEventListener("mousedown", onPointerDownMoving);
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerCancel);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);

        LOGGER.debug("Tool down: {0}", pos);
    }

    function onGuideMove(e: PointerEvent) {
        if (e.buttons !== 1) return;
        int.setSymmetryCenter2(getGuidePos(e));
    }

    function onGuideUp(e: PointerEvent) {
        if (e.button !== 0) return;
        int.setSymmetryCenter2(getGuidePos(e));
        symmetryGuideEditing.set(false);
        unregisterGuide();
    }

    function onGuideCancel() {
        symmetryGuideEditing.set(false);
        unregisterGuide();
    }

    function onGuideKeyDown(e: KeyboardEvent) {
        if (e.key !== "Escape") return;
        symmetryGuideEditing.set(false);
        unregisterGuide();
    }

    function unregisterGuide() {
        window.removeEventListener("pointermove", onGuideMove);
        window.removeEventListener("pointerup", onGuideUp);
        window.removeEventListener("pointercancel", onGuideCancel);
        window.removeEventListener("keydown", onGuideKeyDown);
    }

    function onPointerDownMoving(e: MouseEvent) {
        if (e.buttons & 2) {
            COMMANDS.invoke("editor.tool.cancel");
            return;
        }
    }

    function onPointerMove(e: PointerEvent) {
        if (e.buttons !== 1) return onPointerHover(e);

        const info = {
            shift: e.shiftKey,
            ctrl: e.ctrlKey,
            alt: e.altKey,
        };

        int.pointerMove(getPos(e), info);
    }

    function onPointerHover(e: PointerEvent) {
        const pos = getPos(e);
        canvas.on_pointer_move(pos.x, pos.y);
    }

    function onPointerUp(e: PointerEvent) {
        if (e.button !== 0) return;

        const info = {
            shift: e.shiftKey,
            ctrl: e.ctrlKey,
            alt: e.altKey,
        };

        int.pointerUp(getPos(e), info);
        unregister();

        if (originalTool !== null) {
            const tool = availableTools.value.find((t) => t.index === originalTool);
            if (tool) {
                COMMANDS.invoke(`editor.tool.${tool.id}` as any);
            }
            originalTool = null;
            switchKey = null;
        }
    }

    function onPointerCancel() {
        int.pointerCancel();
        unregister();

        if (originalTool !== null) {
            const tool = availableTools.value.find((t) => t.index === originalTool);
            if (tool) {
                COMMANDS.invoke(`editor.tool.${tool.id}` as any);
            }
            originalTool = null;
            switchKey = null;
        }
    }

    function unregister() {
        window.removeEventListener("mousedown", onPointerDownMoving);
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerCancel);
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("keyup", onKeyUp);
    }

    function onKeyDown(e: KeyboardEvent) {
        if ((e.key === "Control" || (e.key === "Alt" && !isMoveToolActive())) && originalTool === null) {
            const toolId = e.key === "Control" ? "move" : "picker";
            const targetTool = availableTools.value.find((t) => t.id === toolId);
            if (targetTool && int.tool !== targetTool.index && !int.getIsCurrentToolActive()) {
                originalTool = int.tool;
                switchKey = e.key;
                COMMANDS.invoke(`editor.tool.${targetTool.id}` as any);
            }
        }
    }

    function onKeyUp(e: KeyboardEvent) {
        if (e.key === switchKey && originalTool !== null) {
            const tool = availableTools.value.find((t) => t.index === originalTool);
            if (tool) {
                COMMANDS.invoke(`editor.tool.${tool.id}` as any);
            }
            originalTool = null;
            switchKey = null;
        }
    }

    function block(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    function isMoveToolActive() {
        const moveTool = availableTools.value.find((t) => t.id === "move");
        return moveTool?.index === int.tool;
    }

    const destroy = COMMANDS.registerAction(
        "editor.tool.cancel",
        () => {
            if (symmetryGuideEditing.value) {
                symmetryGuideEditing.set(false);
                unregisterGuide();
                return;
            }
            onPointerCancel();
        },
        "all"
    );

    node.addEventListener("pointerdown", onPointerDown);
    node.addEventListener("pointermove", onPointerHover);
    node.addEventListener("contextmenu", block);

    return {
        destroy() {
            node.removeEventListener("pointerdown", onPointerDown);
            node.removeEventListener("pointermove", onPointerHover);
            node.removeEventListener("contextmenu", block);
            unregisterGuide();
            destroy();
        },
    };
}
